import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@material-ui/core';
import { AlertMessage2 } from 'components/AlertMessage2';
import useMembershipDetailsStyles from 'components/Memberships/Detail/MembershipDetailsStyles';
type Classes = ReturnType<typeof useMembershipDetailsStyles>;

export interface EnrollmentsPagination {
    totalEnrollments: number;
    currentPage: number;
    totalPages: number;
    size: number;
}

export interface EnrollmentFilterOption {
    id: string;
    name: React.ReactNode;
    selectedBackgroundColor?: string;
}

interface UseEnrollmentFiltersProps {
    loading: boolean;
    lastFetchedStatus: string;
    enrollmentsPagination: EnrollmentsPagination | null;
}

export const useEnrollmentFilters = ({
    loading,
    lastFetchedStatus,
    enrollmentsPagination
}: UseEnrollmentFiltersProps) => {
    const showCount = (optionId: string) =>
        optionId === lastFetchedStatus && !loading && typeof enrollmentsPagination?.totalEnrollments === 'number';

    const getFilterOptions = (): EnrollmentFilterOption[] => [
        {
            id: 'past_due',
            name: showCount('past_due') ? (
                `Past Due (${enrollmentsPagination?.totalEnrollments ?? 0})`
            ) : (
                <>
                    <FontAwesomeIcon
                        style={{ position: 'relative', left: -5, color: '#E36E74' }}
                        icon={faExclamationCircle}
                    />{' '}
                    Past Due
                </>
            ),
            selectedBackgroundColor: '#E36E74'
        },
        {
            id: 'active',
            name: showCount('active') ? `Active (${enrollmentsPagination?.totalEnrollments ?? 0})` : 'Active'
        },
        {
            id: 'pending',
            name: showCount('pending') ? `Pending (${enrollmentsPagination?.totalEnrollments ?? 0})` : 'Pending'
        },
        {
            id: 'canceled',
            name: showCount('canceled') ? `Canceled (${enrollmentsPagination?.totalEnrollments ?? 0})` : 'Canceled'
        },
        {
            id: 'all',
            name: showCount('all') ? `All (${enrollmentsPagination?.totalEnrollments ?? 0})` : 'All'
        }
    ];

    const getAlerts = (classes: Classes) => {
        if (lastFetchedStatus !== 'pending' && lastFetchedStatus !== 'past_due') return null;

        return (
            <>
                {lastFetchedStatus === 'pending' && (
                    <AlertMessage2
                        icon={faExclamationCircle}
                        iconColor="#BAA997"
                        bgColor="#F8F5F1"
                        bdColor="#BAA997"
                        className={classes.alertMessage}
                    >
                        <Typography>
                            <b>Pending:</b> These memberships are pending payment via Pay by Link. You can collect the
                            payment here, or cancel the membership if needed.
                        </Typography>
                    </AlertMessage2>
                )}

                {lastFetchedStatus === 'past_due' && (
                    <AlertMessage2
                        icon={faExclamationCircle}
                        iconColor="#BAA997"
                        bgColor="#F8F5F1"
                        bdColor="#BAA997"
                        className={classes.alertMessage}
                    >
                        <Typography>
                            <b>Past Due:</b> Payments for these memberships weren&apos;t successfully processed by the
                            due date, leaving them in an overdue status. To resolve this, you can update the payment
                            method or cancel the membership as needed.
                        </Typography>
                    </AlertMessage2>
                )}
            </>
        );
    };

    return {
        getFilterOptions,
        getAlerts
    };
};
